import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, User } from "lucide-react";
import { Eyebrow, Reveal, RegistrationMark } from "../components/Shared";
import { COMPANY, PROJECT_TYPES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", company: "", phone: "", email: "", project_type: "", message: "" };

const inputClass =
  "w-full rounded-lg border border-line bg-white px-4 py-3.5 text-sm text-navy placeholder:text-slate/70 outline-none transition-[border-color,box-shadow] duration-200 focus:border-brand2 focus:ring-2 focus:ring-brand2/15";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="label-caps mb-2 block text-[10px]">{label}</span>
    {children}
  </label>
);

const MapPlaceholder = () => (
  <div
    className="relative flex h-64 flex-col justify-between overflow-hidden rounded-2xl border border-line bg-tint p-6"
    data-testid="location-map"
  >
    <div className="grid-lines absolute inset-0" />
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <span className="absolute -inset-6 animate-ping rounded-full border border-brand2/40" />
      <MapPin size={36} className="relative text-brand2" />
    </div>
    <div className="pointer-events-none absolute right-4 top-4 opacity-50">
      <RegistrationMark size={32} />
    </div>
    <p className="label-caps relative text-[10px]">Location</p>
    <div className="relative rounded-xl border border-line bg-white/90 p-4 backdrop-blur-xl">
      <p className="font-display text-sm font-bold text-navy">{COMPANY.name}</p>
      <p className="mt-1 text-xs leading-relaxed text-slate">{COMPANY.addressLines.join(" ")}</p>
    </div>
  </div>
);

const ContactSection = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received. Our team will reach out to you shortly.");
      setForm(initialForm);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("You've submitted several requests recently. Please wait a few minutes or call us directly.");
      } else if (status === 422 || status === 413) {
        toast.error("Please check your details — one of the fields is missing or too long.");
      } else {
        toast.error("Something went wrong. Please try again or call us directly.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative bg-canvas py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <Eyebrow>07 — Request a Quote</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-2xl font-bold leading-tight tracking-tight text-navy sm:text-3xl lg:text-4xl">
                Let's Bring Your Next Print Project to Life.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-slate">
                Share your requirements and our team will respond with a considered, transparent quote.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="card mt-10 space-y-6 p-8">
                <div className="flex gap-4">
                  <MapPin size={18} className="mt-1 shrink-0 text-brand2" />
                  <div>
                    <p className="label-caps text-[10px]">Address</p>
                    <p className="mt-2 font-display text-base font-bold text-navy">{COMPANY.name}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate">
                      {COMPANY.addressLines.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Phone size={18} className="mt-1 shrink-0 text-brand2" />
                  <div>
                    <p className="label-caps text-[10px]">Phone</p>
                    <a
                      href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                      data-testid="contact-phone"
                      className="mt-2 block text-sm font-medium text-navy transition-colors hover:text-brand2"
                    >
                      {COMPANY.phone}
                    </a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <User size={18} className="mt-1 shrink-0 text-brand2" />
                  <div>
                    <p className="label-caps text-[10px]">Chief Executive Officer</p>
                    <p className="mt-2 text-sm font-medium text-navy">{COMPANY.ceo}</p>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.4} className="mt-6">
              <MapPlaceholder />
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <form
              onSubmit={handleSubmit}
              data-testid="quote-form"
              className="card p-8 sm:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Name *" testid="field-name">
                  <input
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={update("name")}
                    data-testid="quote-name-input"
                    className={inputClass}
                    placeholder="Your full name"
                  />
                </Field>
                <Field label="Company" testid="field-company">
                  <input
                    maxLength={160}
                    value={form.company}
                    onChange={update("company")}
                    data-testid="quote-company-input"
                    className={inputClass}
                    placeholder="Company name"
                  />
                </Field>
                <Field label="Phone *" testid="field-phone">
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="quote-phone-input"
                    className={inputClass}
                    placeholder="Contact number"
                  />
                </Field>
                <Field label="Email *" testid="field-email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="quote-email-input"
                    className={inputClass}
                    placeholder="you@company.com"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Project Type *" testid="field-project-type">
                    <select
                      required
                      value={form.project_type}
                      onChange={update("project_type")}
                      data-testid="quote-project-type-select"
                      className={`${inputClass} appearance-none ${form.project_type ? "" : "text-slate/70"}`}
                    >
                      <option value="" disabled>
                        Select a project type
                      </option>
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Message" testid="field-message">
                    <textarea
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      data-testid="quote-message-input"
                      className={`${inputClass} resize-none`}
                      placeholder="Tell us about your project requirements"
                    />
                  </Field>
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit-btn"
                className="btn-primary group mt-8 w-full disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Request a Quote"}
                {!submitting && (
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                )}
              </button>
              <p className="mt-4 text-center text-xs text-slate">
                Response from our team — direct and transparent
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
